import React, { useState, useEffect } from 'react';
import { Icon, Table, Button, Modal, Input, Breadcrumb, Message, Segment, Label, Confirm } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import _ from 'lodash';
import Page from '../../../components/Page';
import MyMenu from '../menu';
import AccountProgress from '../../../components/AccountProgress';
import CreateModal from '../../../components/CreateWabaTemplate';
import { post } from '../../../util/api';

const WhatsAppTemplates = (props) => {
	const { t } = props;
	const location = useLocation();
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [error, setError] = useState('');
	const [buttonLoading, setButtonLoading] = useState(false);

	const getPhoneNumber = () => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get('number') || '';
	};

	useEffect(() => {
		fetchTemplates();
	}, []);

	const fetchTemplates = async () => {
		try {
			const phoneNumber = getPhoneNumber();
			if (!phoneNumber) {
				setError(t('phoneNumberRequired'));
				setLoading(false);
				return;
			}
			const response = await post(`/messenger/waba/message_template/list/${phoneNumber}`);
			if (response && response.data && response.data.data) {
				setTemplates(response.data.data);
			}
			setLoading(false);
		} catch (error) {
			console.error('Error fetching templates:', error);
			setError(t('errorFetchingTemplates'));
			setLoading(false);
		}
	};

	const handleEditTemplate = (template) => {
		setSelectedTemplate(template);
		setModalOpen(true);
	};

	const handleDeleteClick = (template) => {
		setSelectedTemplate(template);
		setDeleteModalOpen(true);
	};

	const handleDeleteConfirm = async () => {
		setButtonLoading(true);
		try {
			const phoneNumber = getPhoneNumber();
			if (!phoneNumber) {
				setError(t('phoneNumberRequired'));
				return;
			}

			// Template name ve id'sini query parametresi olarak gönderiyoruz
			const response = await post(`/messenger/waba/message_template/delete/${phoneNumber}`, {
				hsm_id: selectedTemplate.id, // Template ID'si
				name: selectedTemplate.name, // Template adı
			});
			console.log('response', response);
			if (response && response.data && response.data.success) {
				await fetchTemplates();
				setError('');
			} else {
				setError(t('errorDeletingTemplate'));
			}
		} catch (error) {
			console.error('Error deleting template:', error);
			setError(t('errorDeletingTemplate'));
		} finally {
			setDeleteModalOpen(false);
			setSelectedTemplate(null);
			setButtonLoading(false);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteModalOpen(false);
		setSelectedTemplate(null);
	};

	const handleModalClose = () => {
		setModalOpen(false);
		setSelectedTemplate(null);
	};

	const handleTemplateCreated = async (success) => {
		if (success) {
			await fetchTemplates();
			setModalOpen(false);
			setSelectedTemplate(null);
		}
	};

	const handleSearchChange = (e, { value }) => {
		setSearchTerm(value);
	};

	const filteredTemplates = templates.filter((template) => {
		const searchLower = searchTerm.toLowerCase();
		return (
			template.name.toLowerCase().includes(searchLower) ||
			(template.category && template.category.toLowerCase().includes(searchLower)) ||
			_.get(template, 'components[0].text', '').toLowerCase().includes(searchLower)
		);
	});

	if (loading) {
		return <Page.Loader />;
	}

	return (
		<Page
			header={
				<Breadcrumb size="big">
					<Breadcrumb.Section as={Link} to={`/settings`}>
						<Icon name="setting" />
						{t('settings')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section as={Link} to={`/settings/integrations`}>
						{t('integrations')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section>WhatsApp Business</Breadcrumb.Section>
				</Breadcrumb>
			}
			extra={
				<div className="ml-auto">
					<AccountProgress />
				</div>
			}
			flex
		>
			<MyMenu activeItem="whatsapp">
				<Page.Wrap>
					<Segment>
						{error && (
							<Message negative>
								<Message.Header>{error}</Message.Header>
							</Message>
						)}

						<div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Input icon="search" placeholder={t('searchByName')} style={{ width: '300px' }} value={searchTerm} onChange={handleSearchChange} />
							<Button primary onClick={() => setModalOpen(true)}>
								{t('addTemplate')}
							</Button>
						</div>

						<Table celled>
							<Table.Header>
								<Table.Row>
									<Table.HeaderCell>{t('name')}</Table.HeaderCell>
									<Table.HeaderCell>{t('category')}</Table.HeaderCell>
									<Table.HeaderCell>{t('preview')}</Table.HeaderCell>
									<Table.HeaderCell>{t('syncStatus')}</Table.HeaderCell>
									<Table.HeaderCell>{t('status')}</Table.HeaderCell>
									<Table.HeaderCell>{t('languages')}</Table.HeaderCell>
									<Table.HeaderCell>{t('actions')}</Table.HeaderCell>
								</Table.Row>
							</Table.Header>

							<Table.Body>
								{filteredTemplates.map((template) => (
									<Table.Row key={template.id}>
										<Table.Cell>{template.name}</Table.Cell>
										<Table.Cell>{template.category}</Table.Cell>
										<Table.Cell>{_.get(template, 'components[0].text', '')}</Table.Cell>
										<Table.Cell>
											<Icon name="check circle" color="green" />
											{t('synced')}
										</Table.Cell>
										<Table.Cell>
											<Label color={template.status === 'APPROVED' ? 'green' : 'yellow'}>{template.status}</Label>
										</Table.Cell>
										<Table.Cell>{template.language}</Table.Cell>
										<Table.Cell>
											<Button.Group>
												<Button icon="edit" onClick={() => handleEditTemplate(template)} title={t('edit')} />
												<Button icon="trash" onClick={() => handleDeleteClick(template)} title={t('delete')} />
											</Button.Group>
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>

						<CreateModal
							modalOpen={modalOpen}
							onClose={handleModalClose}
							onTemplateCreated={handleTemplateCreated}
							template={selectedTemplate}
							t={t}
							phoneNumber={getPhoneNumber()}
						/>

						<Modal open={deleteModalOpen} size="tiny" onClose={handleDeleteCancel}>
							<Modal.Header>{t('confirmDeleteTemplate')}</Modal.Header>
							<Modal.Content>
								<p>{t('deleteTemplateWarning')}</p>
							</Modal.Content>
							<Modal.Actions>
								<Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
								<Button negative loading={buttonLoading} disabled={buttonLoading} onClick={handleDeleteConfirm}>
									{t('delete')}
								</Button>
							</Modal.Actions>
						</Modal>
					</Segment>
				</Page.Wrap>
			</MyMenu>
		</Page>
	);
};

const stateToProps = (state) => ({
	access: state.access,
	product: state.product,
	location: state.router.location,
});

export default connect(stateToProps)(withTranslation()(WhatsAppTemplates));
